import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Skeleton, Chip } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { blue, green, red, grey } from '@mui/material/colors';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import api from '../../../services/api';

const PERIODS = [
    { value: '7d',  label: '7d' },
    { value: '30d', label: '30d' },
    { value: '90d', label: '90d' },
];

const fmtDate = (s) => {
    const d = new Date(s + 'T00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

// Média simples da primeira e da segunda metade da série pra detectar tendência.
const detectTrend = (series) => {
    if (series.length < 4) return { dir: 'flat', delta: 0 };
    const mid = Math.floor(series.length / 2);
    const avgFirst = series.slice(0, mid).reduce((s, d) => s + (d.covered || 0), 0) / mid;
    const avgSecond = series.slice(mid).reduce((s, d) => s + (d.covered || 0), 0) / (series.length - mid);
    const delta = avgSecond - avgFirst;
    const pct = avgFirst > 0 ? (delta / avgFirst * 100) : 0;
    if (Math.abs(pct) < 5) return { dir: 'flat', delta: pct };
    return { dir: delta > 0 ? 'up' : 'down', delta: pct };
};

export default function BackupVolumeTrend() {
    const [period, setPeriod] = useState('30d');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/hermes/dashboard/coverage_trend?period=${period}`)
            .then(r => setData(r.data || []))
            .finally(() => setLoading(false));
    }, [period]);

    const totalActive = data[0]?.total ?? null;
    const avgCovered = data.length
        ? Math.round(data.reduce((s, d) => s + (d.covered || 0), 0) / data.length)
        : 0;
    const avgFailed = data.length
        ? Math.round(data.reduce((s, d) => s + (d.failed || 0), 0) / data.length)
        : 0;
    const trend = detectTrend(data);
    const TrendIcon = trend.dir === 'up' ? TrendingUpIcon : trend.dir === 'down' ? TrendingDownIcon : TrendingFlatIcon;
    const trendColor = trend.dir === 'up' ? green[600] : trend.dir === 'down' ? red[600] : grey[600];

    return (
        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                <Box>
                    <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500 }}>
                        Hosts com config nova por dia
                    </Typography>
                    {!loading && data.length > 0 && (
                        <Box display="flex" alignItems="center" gap={1.5} mt={0.5} flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                <Box component="span" sx={{ color: blue[700], fontWeight: 600 }}>
                                    {avgCovered}
                                </Box>{' '}com config nova
                                {avgFailed > 0 && (
                                    <>{' · '}
                                        <Box component="span" sx={{ color: red[700], fontWeight: 600 }}>
                                            {avgFailed}
                                        </Box>{' '}falharam
                                    </>
                                )}{' '}/ dia (média)
                                {totalActive ? ` · base ${totalActive}` : ''}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.3}>
                                <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
                                <Typography variant="caption" sx={{ color: trendColor }}>
                                    {trend.dir === 'flat' ? 'estável' :
                                     trend.dir === 'up' ? `+${Math.abs(trend.delta).toFixed(0)}%` :
                                     `-${Math.abs(trend.delta).toFixed(0)}%`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box display="flex" gap={0.5}>
                    {PERIODS.map(p => (
                        <Chip key={p.value} label={p.label} size="small"
                              onClick={() => setPeriod(p.value)}
                              color={period === p.value ? 'primary' : 'default'}
                              variant={period === p.value ? 'filled' : 'outlined'} />
                    ))}
                </Box>
            </Box>

            {loading ? (
                <Skeleton variant="rectangular" height={220} />
            ) : data.length === 0 ? (
                <Box height={220} display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="body2" color="text.secondary">
                        Sem dados no período.
                    </Typography>
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={20} />
                        <YAxis allowDecimals={false} />
                        <Tooltip labelFormatter={fmtDate}
                                 formatter={(v, name) => [`${v} hosts`, name === 'covered' ? 'Config nova' : 'Falharam']} />
                        <Legend formatter={(v) => v === 'covered' ? 'Config nova' : 'Falharam'} />
                        {totalActive && (
                            <ReferenceLine y={totalActive} stroke={grey[400]} strokeDasharray="4 4"
                                           label={{ value: `base ${totalActive}`, position: 'right', fill: grey[600], fontSize: 11 }} />
                        )}
                        <Line type="monotone" dataKey="covered" name="covered"
                              stroke={blue[600]} strokeWidth={2}
                              dot={{ r: 3, fill: blue[600] }} activeDot={{ r: 5 }} />
                        <Line type="monotone" dataKey="failed" name="failed"
                              stroke={red[600]} strokeWidth={2}
                              dot={{ r: 3, fill: red[600] }} activeDot={{ r: 5 }} />
                    </LineChart>
                </ResponsiveContainer>
            )}
        </Paper>
    );
}
